import { useEffect, useState } from "react";
import {
  Box,
  Button,
  HStack,
  Input,
  List,
  ListItem,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { CreatorsApi, type CreatorProfileRow, type CreatorRow } from "../api/creators";
import logger from "../analytics/logger";

export type CreatorProfileValue = { creatorId: string; profileId: string };

type Props = {
  value: CreatorProfileValue;
  onChange: (v: CreatorProfileValue) => void;
  disabled?: boolean;
  /** Append a red "*" to the labels. */
  requiredMark?: boolean;
  creatorError?: string;
  profileError?: string;
};

/**
 * Creator typeahead → profile select, extracted from AddWorkModal so Add Work
 * and Works bulk-edit share the exact same picker UX. The component owns its
 * search/profile-loading state; the parent holds `{creatorId, profileId}` and
 * reads them for validation/submit. Clearing `value.creatorId` externally
 * resets the picker.
 */
export default function CreatorProfilePicker({
  value,
  onChange,
  disabled,
  requiredMark,
  creatorError,
  profileError,
}: Props) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CreatorRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [creatorLabel, setCreatorLabel] = useState("");
  const [profiles, setProfiles] = useState<CreatorProfileRow[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  const mark = requiredMark ? (
    <Text as="span" color="red.500" ml={1}>
      *
    </Text>
  ) : null;

  // Reset internal state when the creator is cleared by the parent.
  const [prevCreatorId, setPrevCreatorId] = useState(value.creatorId);
  if (value.creatorId !== prevCreatorId) {
    setPrevCreatorId(value.creatorId);
    if (!value.creatorId) {
      setQuery("");
      setResults([]);
      setSearching(false);
      setCreatorLabel("");
      setProfiles([]);
      setLoadingProfiles(false);
    }
  }

  // Kick off the pending-search UI at render time (spinner on for a new term,
  // stale results cleared when the box is emptied) so the debounced effect below
  // holds no synchronous setState. The key is empty while a creator is selected.
  const searchKey = value.creatorId ? "" : query.trim();
  const [prevSearchKey, setPrevSearchKey] = useState(searchKey);
  if (searchKey !== prevSearchKey) {
    setPrevSearchKey(searchKey);
    if (searchKey) {
      setSearching(true);
    } else {
      setResults([]);
    }
  }

  // Debounced creator search; skips once a creator is selected.
  useEffect(() => {
    if (value.creatorId) return;
    const term = query.trim();
    if (!term) return;
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const res = await CreatorsApi.list({ page: 1, page_size: 10, q: term });
        if (!cancelled) setResults(res.creators);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(h);
    };
  }, [query, value.creatorId]);

  const selectCreator = async (c: CreatorRow) => {
    setCreatorLabel(c.email ? `${c.name} (${c.email})` : c.name);
    setResults([]);
    setQuery("");
    onChange({ creatorId: c.id, profileId: "" });
    setLoadingProfiles(true);
    try {
      const detail = await CreatorsApi.get(c.id);
      setProfiles(detail.profiles);
      // Auto-select when there's exactly one profile.
      if (detail.profiles.length === 1) {
        onChange({ creatorId: c.id, profileId: detail.profiles[0].id });
      }
    } catch (e: unknown) {
      logger.error("creator_profile_picker_load_failed", e);
      setProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const clear = () => onChange({ creatorId: "", profileId: "" });

  const noProfiles = !!value.creatorId && !loadingProfiles && profiles.length === 0;

  return (
    <>
      <Box>
        <Text fontSize="sm" mb={1}>
          Creator{mark}
        </Text>
        {value.creatorId ? (
          <HStack>
            <Text flex="1" noOfLines={1}>
              {creatorLabel || value.creatorId}
            </Text>
            <Button size="sm" variant="ghost" onClick={clear} isDisabled={disabled}>
              Change
            </Button>
          </HStack>
        ) : (
          <Box position="relative">
            <Input
              placeholder="Search name or email…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              isDisabled={disabled}
              isInvalid={!!creatorError}
            />
            {searching && (
              <Spinner size="sm" position="absolute" right="10px" top="10px" />
            )}
            {results.length > 0 && (
              <List
                position="absolute"
                zIndex={10}
                bg="white"
                w="100%"
                mt={1}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="md"
                maxH="220px"
                overflowY="auto"
              >
                {results.map((c) => (
                  <ListItem
                    key={c.id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "gray.100" }}
                    onClick={() => selectCreator(c)}
                  >
                    <Text fontSize="sm" fontWeight="medium">
                      {c.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {c.email}
                    </Text>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
        {creatorError && (
          <Text color="red.500" fontSize="xs" mt={1}>
            {creatorError}
          </Text>
        )}
      </Box>

      <Box>
        <Text fontSize="sm" mb={1}>
          Profile{mark}
        </Text>
        <Select
          placeholder={loadingProfiles ? "Loading…" : "Select profile"}
          value={value.profileId}
          onChange={(e) => onChange({ ...value, profileId: e.target.value })}
          isDisabled={disabled || !value.creatorId || loadingProfiles || noProfiles}
          isInvalid={!!profileError}
        >
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>
              {p.profile_name}
            </option>
          ))}
        </Select>
        {noProfiles && (
          <Text color="orange.500" fontSize="xs" mt={1}>
            This creator has no profiles — add one on the creator page first.
          </Text>
        )}
        {profileError && (
          <Text color="red.500" fontSize="xs" mt={1}>
            {profileError}
          </Text>
        )}
      </Box>
    </>
  );
}
